import { Controller } from '@hotwired/stimulus';
import { application } from 'src';
import { findAll, addClass, removeClass } from '@fretadao/f-js-dom';

application.register(
  'filter-table',
  class extends Controller {
    static targets = ['table', 'filter'];
    static classes = ['hidden', 'rowBg'];

    connect() {
      this.filterCallback = this.filter.bind(this);

      this.filterTargets.forEach(el => {
        el.addEventListener('input', this.filterCallback);
      });

      this.filter();
    }

    disconnect() {
      this.filterTargets.forEach(el => {
        el.removeEventListener('input', this.filterCallback);
      });
    }

    filter() {
      this.#resetTableRows();
      this.#filterRows();
      this.#setBackgroundForRows();
    }

    #resetTableRows() {
      const desktopHiddenRows = `tbody tr.${this.hiddenClass}`;
      const mobileHiddenRows = `#mobile-table details.${this.hiddenClass}`;

      findAll(`${desktopHiddenRows}, ${mobileHiddenRows}`, this.tableTarget).forEach(row => {
        removeClass(row, this.hiddenClass);
        this.#enableInputs(row);
      });
    }

    #filterRows() {
      this.filterTargets.forEach(filter => {
        const columnName = filter.dataset.column;
        const query = filter.value.toString().toLowerCase();

        this.#filterDesktopTableRows(columnName, query);
        this.#filterMobileTableRows(columnName, query);
      });
    }

    #setBackgroundForRows() {
      const visibleRows = findAll(`tbody tr:not(.${this.hiddenClass})`, this.tableTarget);

      visibleRows.forEach((row, index) => {
        if (index % 2 !== 0) addClass(row, this.rowBgClass);
        else removeClass(row, [this.rowBgClass, `even:${this.rowBgClass}`]);
      });
    }

    #filterDesktopTableRows(columnName, query) {
      const columnIndex = this.#getColumnIndex({
        columnName: columnName,
        columnPath: 'thead th',
      });
      const visibleColumns = `tbody tr:not(.${this.hiddenClass}) > td:nth-child(${columnIndex})`;

      findAll(visibleColumns, this.tableTarget).forEach(cell => {
        this.#filterRowIfSearchMatches({ text: cell.textContent, row: cell.parentNode, query });
      });
    }

    #filterMobileTableRows(columnName, query) {
      const columnIndex = this.#getColumnIndex({
        columnName: columnName,
        columnPath: '#mobile-table details [data-mobile-col] strong',
      });
      const visibleColumns = `#mobile-table details:not(.${this.hiddenClass}) [data-mobile-col]:nth-child(${columnIndex}) > p`;

      findAll(visibleColumns, this.tableTarget).forEach(cell => {
        this.#filterRowIfSearchMatches({
          text: cell.textContent,
          row: cell.closest('details'),
          query,
        });
      });
    }

    #filterRowIfSearchMatches({ row, text, query }) {
      const searchMatched = text.trim().toLowerCase().includes(query);

      if (searchMatched) {
        removeClass(row, this.hiddenClass);
        this.#enableInputs(row);
      } else {
        addClass(row, this.hiddenClass);
        this.#disableInputs(row);
      }
    }

    #getColumnIndex({ columnName, columnPath }) {
      const index = findAll(columnPath, this.tableTarget).findIndex(header => {
        return header.textContent.toLowerCase() === columnName.toLowerCase();
      });

      if (index === -1) throw `Column ${columnName} not found!`;

      // Because CSS's nth-child is 1-indexed, not 0-indexed
      return index + 1;
    }

    #disableInputs(row) {
      this.#setInputsDisabledTo(row, true);
    }

    #enableInputs(row) {
      this.#setInputsDisabledTo(row, false);
    }

    #setInputsDisabledTo(row, value) {
      findAll('input[type=checkbox]:not(:checked)', row).forEach(el => {
        el.disabled = value;
      });
    }
  }
);
